import { getLogger } from "@expert/logging";
import { HTTPError } from "ky";
import { getHashedValue, removeCountryCode } from "@/shared-utils";
import { getCustomerDetailsApi } from "./api";
import type {
    BillingAccountNumberGetCustomerDetailsParams,
    CustomerDetailsPayload,
    CustomerDetailsV3RequestBody,
    GetCustomerDetailsV3Params,
    PhoneNumberGetCustomerDetailsParams,
} from "./types";

const logger = getLogger({
    module: "customerDetailsV3",
    supportTeam: "sales",
});

export const getCustomerDetailsV3 = async (
    params: GetCustomerDetailsV3Params,
): Promise<CustomerDetailsPayload | null> => {
    const { modelResultsMdn, partner, searchType, dispatcher, customerDetailsCallback } = params;
    let jsonPayload: CustomerDetailsV3RequestBody;
    const formattedMdn = modelResultsMdn && removeCountryCode(modelResultsMdn);
    const hashedMdn = formattedMdn && (await getHashedValue(formattedMdn));

    if (isValidPhoneNumberSearchType(params)) {
        jsonPayload = {
            partner,
            searchType,
            criteria: {
                phoneNumber: removeCountryCode(params.mdn),
            },
            hashedMdn,
        };
    } else if (isValidBillingAccountNumberAndPinSearchType(params)) {
        jsonPayload = {
            partner,
            searchType,
            criteria: {
                billingAccountNumber: params.billingAccountNumber,
                customerVerificationMethod: params.pin ? "PIN" : "BANV",
                pin: params.pin,
            },
            hashedMdn,
        };
    } else {
        return null;
    }

    try {
        const client = getCustomerDetailsApi();

        const apiPath = "v3/customer/details";

        const response = await client.post(apiPath, { json: jsonPayload, retry: 0 }).json<CustomerDetailsPayload>();

        // only update the MDN if it was provided in the initial request
        if (response.primaryAccountHolder?.mdn && isValidPhoneNumberSearchType(params)) {
            response.primaryAccountHolder.mdn = params.mdn;
        }
        void dispatcher?.dispatchBusinessEvent("CustomerDetailsStats", {
            includesPrimaryAccountHolder: !!response.primaryAccountHolder,
            includesAccountNumber: !!response.primaryAccountHolder?.accountNumber,
            includesCustomerAddress: !!response.primaryAccountHolder?.address,
            includesDeviceReplacementDetails: !!response.deviceActivation,
            includesPreviousSummary: !!response.previousSummary,
            previousDeviceCount: response.previousDevices?.length ?? 0,
        });

        customerDetailsCallback?.(response);

        return response;
    } catch (err: unknown) {
        if (err instanceof HTTPError) {
            logger.error(`getCustomerDetails API call failed with status: ${err.response.status}`);
            return null;
        }
        logger.error({ err }, `getCustomerDetails API call failed with exception`);
        return null;
    }
};

function isValidPhoneNumberSearchType(
    params: GetCustomerDetailsV3Params,
): params is PhoneNumberGetCustomerDetailsParams {
    return params.searchType === "phoneNumber" && params.mdn != null;
}

function isValidBillingAccountNumberAndPinSearchType(
    params: GetCustomerDetailsV3Params,
): params is BillingAccountNumberGetCustomerDetailsParams {
    return params.searchType === "billingAccountNumberAndPin" && params.billingAccountNumber != null;
}
